import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu, X, Package, TrendingUp, Inbox, BarChart3, LogOut, ShoppingCart, Boxes, Truck, MapPin,
  Calculator, Building2, Store, Warehouse, Users, Shield, ShieldAlert, AlertTriangle, ChevronDown,
  ChevronRight, Bell, User, Settings, DollarSign, Sparkles, Camera, Globe, Trophy, RotateCcw,
  MessageCircle, Fingerprint, LayoutDashboard, Search, PackageCheck, CornerDownLeft, AlertCircle, Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n';
import axios from 'axios';
import DevModeBadge from './developer/DevModeBadge';
import ScrollToTop from './ScrollToTop';
import DiyalogLogo from './brand/DiyalogLogo';
const DeveloperDocEditorLazy = lazy(() => import('./developer/DeveloperDocEditor'));

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fiori Launchpad ust modul gruplari (yatay grup seridi). Her nav ogesi bir gruba baglanir.
const NAV_GROUPS = [
  { key: 'genel', label: 'Genel Bakış', icon: LayoutDashboard, home: '/' },
  { key: 'depo', label: 'Depo & Stok', icon: Warehouse },
  { key: 'siparis', label: 'Sipariş', icon: ShoppingCart },
  { key: 'toplama', label: 'Toplama & Paketleme', icon: PackageCheck },
  { key: 'sevkiyat', label: 'Sevkiyat', icon: Truck },
  { key: 'operasyon', label: 'Operasyon', icon: Boxes },
  { key: 'rapor', label: 'Raporlar', icon: BarChart3 },
  { key: 'yonetim', label: 'Yönetim', icon: Settings },
];

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const isRtl = isRTL(i18n.language);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [openGroup, setOpenGroup] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [taskCounts, setTaskCounts] = useState({
    pending_receipts: 0, pending_putaway: 0, ready_to_pick: 0, to_pack: 0, to_ship: 0, new_orders: 0, picking_problems: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissedTypes, setDismissedTypes] = useState({});
  const [langOpen, setLangOpen] = useState(false);
  const { user, logout, canAccessModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const groupNavRef = useRef(null);
  const searchRef = useRef(null);
  const kioskBufferRef = useRef('');
  const kioskTimerRef = useRef(null);
  const [kioskUnlocked, setKioskUnlocked] = useState(false);
  const [kioskUnlockName, setKioskUnlockName] = useState('');
  const langRef = useRef(null);

  // Kullanıcı koyu temayı kapattı — kalıcı 'dark' sınıfını temizle.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    try { localStorage.setItem('darkMode', 'false'); } catch (_) {}
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const toggleMenu = (name) => setExpandedMenus(prev => ({ ...prev, [name]: !prev[name] }));
  const closeAllMenus = () => { setOpenGroup(null); setNotificationOpen(false); setProfileOpen(false); setLangOpen(false); setSearchOpen(false); };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    const diffHours = Math.floor((now - date) / 3600000);
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffMins < 1) return t('layout.justNow');
    if (diffMins < 60) return t('layout.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('layout.hoursAgo', { count: diffHours });
    return t('layout.daysAgo', { count: diffDays });
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'NEW_ORDER': return 'bg-brand-500';
      case 'BLOCKED_ORDER': return 'bg-warning';
      case 'READY_TO_PICK': return 'bg-infoq';
      case 'TO_PACK': return 'bg-success';
      case 'TO_SHIP': return 'bg-violet-500';
      default: return 'bg-slate-400';
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Cache-Control': 'no-cache' },
        params: { _ts: Date.now() },
      });
      const raw = response.data.notifications || [];
      const DISMISS_TTL = 5 * 60 * 1000;
      const now = Date.now();
      const validDismiss = {};
      Object.keys(dismissedTypes || {}).forEach(k => {
        if ((now - dismissedTypes[k]) < DISMISS_TTL) validDismiss[k] = dismissedTypes[k];
      });
      if (Object.keys(validDismiss).length !== Object.keys(dismissedTypes || {}).length) setDismissedTypes(validDismiss);
      const annotated = raw.map(n => ({ ...n, is_read: !!validDismiss[n.type] }));
      setNotifications(annotated);
      setUnreadCount(annotated.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Notifications fetch error:', error);
      setNotifications([]); setUnreadCount(0);
    }
  };

  const markNotificationRead = (notification) => {
    if (!notification || !notification.type) return;
    setNotifications(prev => prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    setDismissedTypes(prev => ({ ...prev, [notification.type]: Date.now() }));
    try {
      axios.post(`${API_URL}/api/notifications/read`, { notification_id: notification.id, notification_type: notification.type },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => {});
    } catch (_) {}
  };

  const fetchTaskCounts = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTaskCounts({
        pending_receipts: response.data.pending_receipts || 0,
        pending_putaway: response.data.pending_putaway || 0,
        ready_to_pick: response.data.ready_to_pick || 0,
        to_pack: response.data.to_pack || 0,
        to_ship: response.data.to_ship || 0,
        new_orders: response.data.new_orders || 0,
        picking_problems: response.data.picking_problems || 0
      });
    } catch (error) { console.error('Task counts fetch error:', error); }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setNotificationOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target)) setProfileOpen(false);
      if (langRef.current && !langRef.current.contains(event.target)) setLangOpen(false);
      if (groupNavRef.current && !groupNavRef.current.contains(event.target)) setOpenGroup(null);
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) { fetchTaskCounts(); const i = setInterval(fetchTaskCounts, 30000); return () => clearInterval(i); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let timer = null;
    const debouncedRefresh = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => { timer = null; fetchTaskCounts(); }, 1000); };
    const onVisible = () => { if (document.visibilityState === 'visible') debouncedRefresh(); };
    window.addEventListener('wms:counts-stale', debouncedRefresh);
    window.addEventListener('focus', debouncedRefresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('wms:counts-stale', debouncedRefresh);
      window.removeEventListener('focus', debouncedRefresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) { fetchNotifications(); const i = setInterval(fetchNotifications, 60000); return () => clearInterval(i); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Kiosk: tehlikeli kısayolları engelle
  useEffect(() => {
    const blockKeys = (e) => {
      if ((e.ctrlKey && (e.key === 'w' || e.key === 'W' || e.key === 't' || e.key === 'T' || e.key === 'n' || e.key === 'N')) ||
          (e.altKey && e.key === 'F4')) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('keydown', blockKeys, true);
    return () => window.removeEventListener('keydown', blockKeys, true);
  }, []);

  // Kiosk unlock: global barkod dinleyici
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        const code = kioskBufferRef.current.trim();
        if (code.length >= 4 && code.length <= 10) {
          axios.post(`${API_URL}/api/kiosk/verify-unlock`, { code }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then(res => {
              if (res.data.success) {
                setKioskUnlockName(res.data.message || 'Kiosk kilidi acildi');
                setKioskUnlocked(true);
                fetch('http://localhost:9999/kill-chrome', { mode: 'no-cors' }).catch(() => {});
              }
            }).catch(() => {});
        }
        kioskBufferRef.current = '';
        return;
      }
      if (e.key.length === 1) {
        kioskBufferRef.current += e.key;
        clearTimeout(kioskTimerRef.current);
        kioskTimerRef.current = setTimeout(() => { kioskBufferRef.current = ''; }, 2000);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const getTaskCount = (module) => {
    switch (module) {
      case 'inbound': return taskCounts.pending_receipts;
      case 'putaway': return taskCounts.pending_putaway;
      case 'picking': return taskCounts.ready_to_pick;
      case 'packing': return taskCounts.to_pack;
      case 'shipping': return taskCounts.to_ship;
      case 'orders': return taskCounts.new_orders;
      default: return 0;
    }
  };

  // FIX 2026-07-18: "Sorun Raporları" öğesi module='picking' olduğu için badge eskiden
  // ready_to_pick (toplama bekleyen) sayısını gösteriyordu — sorun yokken bile 3385 gibi.
  // Bu öğe için gerçek çözülmemiş sorun sayısını kullan (modülü değiştirmeden, yetki korunur).
  const itemBadge = (item) => (
    item?.href === '/picking-problems'
      ? (taskCounts.picking_problems || 0)
      : getTaskCount(item?.module)
  );

  // Tüm navigasyon — her ögeye Fiori grup anahtarı eklendi
  const allNavigation = [
    { name: t('navigation.dashboard'), href: '/', icon: TrendingUp, module: 'dashboard', group: 'genel' },
    { name: 'Kule — Canlı Depo', href: '/kule', icon: Activity, module: 'dashboard', group: 'toplama', hideForRoles: ['Client'] },
    { name: t('navigation.clients'), href: '/clients', icon: Building2, module: 'clients', group: 'siparis' },
    { name: t('navigation.stores'), href: '/stores', icon: Store, module: 'stores', group: 'siparis' },
    { name: t('navigation.products'), href: '/products', icon: Package, module: 'products', group: 'depo' },
    {
      name: t('navigation.inventory'), icon: Warehouse, module: 'inventory', group: 'depo',
      subItems: [
        { name: t('navigation.inventoryMain', 'Stok Yönetimi'), href: '/inventory', module: 'inventory' },
        { name: t('navigation.itemLookup', 'Ürün Görüntüleme'), href: '/item-lookup', module: 'inventory', hideForRoles: ['Client'] },
        { name: t('navigation.locationLookup', 'Göz Adresi Görüntüleme'), href: '/location-lookup', module: 'inventory', hideForRoles: ['Client'] },
        { name: t('navigation.inventoryTransfer', 'Stok Transfer'), href: '/inventory-transfer', module: 'inventory', hideForRoles: ['Client'] }
      ]
    },
    {
      name: t('navigation.inbound'), icon: Inbox, module: 'inbound', group: 'depo',
      subItems: [
        { name: t('navigation.inboundActive', 'Aktif Mal Kabuller'), href: '/inbound', module: 'inbound' },
        { name: t('navigation.inboundCompleted', 'Tamamlanan Mal Kabuller'), href: '/inbound/completed', module: 'inbound' },
      ]
    },
    { name: t('navigation.putaway'), href: '/putaway', icon: Boxes, module: 'putaway', group: 'depo' },
    { name: t('navigation.orders'), href: '/orders', icon: Package, module: 'orders', group: 'siparis' },
    { name: 'Bloklu Siparişler', href: '/blocked-orders', icon: AlertCircle, module: 'orders', group: 'siparis' },
    {
      name: t('navigation.picking'), icon: ShoppingCart, module: 'picking', group: 'toplama',
      subItems: [
        // 2026-07-21 HEDEF MİMARİ GEÇİŞİ: tekli/çoklu/bench/geçmiş menüden GİZLENDİ
        // (kod + route'lar duruyor; URL ile erişilebilir, gerekirse tek satırla geri açılır).
        // Tek toplama ailesi: Raf Bazlı (tekli) + Çoklu Raf.
        { name: t('navigation.shelfPicking', 'Raf Bazlı Toplama'), href: '/picking/shelf', module: 'picking' },
        { name: 'Çoklu Raf Toplama', href: '/picking/shelf-multi', module: 'picking' },
      ]
    },
    // 2026-07-21: normal Paketleme GEÇİCİ görünür — CART-3 bench işleri bitene kadar
    // (bench görevleri slot-bazlı normal ekrandan paketleniyor). Sonra tekrar gizlenecek.
    // 25 Tem (kullanıcı isteği): eski /packing menüden GİZLENDİ — sadece Raf Bazlı Paketleme
    // kalıyor. Route App.js'te duruyor (bench/multi yönlendirmeleri için) ama menüde yok.
    // { name: t('navigation.packing'), href: '/packing', icon: Package, module: 'packing', group: 'toplama' },
    { name: t('navigation.shelfPacking', 'Raf Bazlı Paketleme'), href: '/packing/shelf', icon: Boxes, module: 'packing', group: 'toplama' },
    { name: 'Geri Yerleştirme', href: '/putback', icon: Package, module: 'putback', hideForRoles: ['Client'], group: 'depo' },
    { name: t('navigation.shipping'), href: '/shipping', icon: Truck, module: 'shipping', group: 'sevkiyat' },
    { name: 'Manuel Teslimat', href: '/shipping/handover', icon: Truck, module: 'shipping', group: 'sevkiyat', hideForRoles: ['Client'] },
    { name: 'Depo Taşıma', href: '/depot-transfer', icon: Truck, module: 'depot_transfer', group: 'depo' },
    {
      name: t('navigation.counting'), icon: Calculator, module: 'counting', group: 'depo',
      subItems: [
        { name: t('navigation.countWarehouse'), href: '/counting/warehouse', module: 'counting' },
        { name: t('navigation.countByClient'), href: '/counting/client', module: 'counting' },
        { name: t('navigation.countByProduct'), href: '/counting/product', module: 'counting' },
        { name: t('navigation.countByLocation'), href: '/counting/location', module: 'counting' }
      ]
    },
    { name: t('navigation.reports'), href: '/reports', icon: BarChart3, module: 'reports', group: 'rapor' },
    { name: t('navigation.cargoCompanies'), href: '/cargo-companies', icon: Truck, module: 'cargo', managerAccess: true, group: 'sevkiyat' },
    { name: t('navigation.charging'), href: '/charging', icon: DollarSign, module: 'charging', group: 'operasyon' },
    {
      name: t('navigation.vas'), icon: Sparkles, module: 'vas', group: 'operasyon',
      subItems: [
        { name: t('navigation.vasDefinitions'), href: '/vas-definitions', module: 'vas' },
        { name: t('navigation.consumableInventory'), href: '/consumable-inventory', module: 'vas' },
        { name: t('navigation.consumableReceipts'), href: '/consumable-receipts', module: 'vas' }
      ]
    },
    { name: t('navigation.damageReport'), href: '/damage-report', icon: AlertTriangle, module: 'damage', group: 'operasyon' },
    { name: t('navigation.pickingProblems', 'Sorun Raporları'), href: '/picking-problems', icon: AlertTriangle, module: 'picking', group: 'toplama' },
    { name: 'İptal Merkezi', href: '/cancellations', icon: X, module: 'cancellations', hideForRoles: ['Client'], group: 'siparis' },
    { name: t('navigation.returns'), href: '/returns', icon: RotateCcw, module: 'returns', group: 'siparis' },
    { name: t('navigation.support', 'Destek'), href: '/support', icon: MessageCircle, module: 'support', group: 'operasyon' },
    { name: t('navigation.competitionReports'), href: '/competition-reports', icon: Trophy, module: 'competition', group: 'operasyon' },
    { name: t('navigation.locations'), href: '/locations', icon: MapPin, module: 'locations', group: 'depo' },
    { name: t('navigation.carts'), href: '/carts', icon: ShoppingCart, module: 'carts', group: 'depo' },
    { name: t('navigation.users'), href: '/users', icon: Users, module: 'users', group: 'yonetim' },
    { name: t('navigation.roles'), href: '/roles', icon: Shield, module: 'roles', superAdminOnly: true, group: 'yonetim' },
    { name: t('navigation.cameraManagement'), href: '/camera-management', icon: Camera, module: 'camera', group: 'yonetim' },
    { name: t('navigation.pdks'), href: '/pdks', icon: Fingerprint, module: 'pdks', group: 'yonetim' },
    { name: t('navigation.settings'), href: '/settings', icon: Settings, module: 'settings', superAdminOnly: true, group: 'yonetim' },
    { name: 'Developer Log', href: '/admin/developer-log', icon: ShieldAlert, module: 'developer_log', group: 'yonetim' },
    { name: 'Yetki Grantları', href: '/admin/permissions', icon: ShieldAlert, module: 'developer_log', group: 'yonetim' },
    { name: 'Watchdog Metrics', href: '/admin/watchdog-metrics', icon: ShieldAlert, module: 'developer_log', group: 'yonetim' },
    { name: 'API Saglik', href: '/admin/api-health', icon: ShieldAlert, developerOnly: true, group: 'yonetim' },
    { name: 'Sürüm Notları', href: '/changelog', icon: Sparkles, allowRoles: ['Developer', 'SuperAdmin'], group: 'yonetim' },
  ];

  const itemVisible = (item) => {
    if (item.developerOnly && user?.role !== 'Developer') return false;
    if (item.superAdminOnly && user?.role !== 'SuperAdmin') return false;
    if (item.allowRoles) return item.allowRoles.includes(user?.role);
    if (item.hideForRoles && item.hideForRoles.includes(user?.role)) return false;
    return canAccessModule(item.module);
  };

  const navigation = allNavigation.filter(itemVisible);
  const visibleSubs = (item) => (item.subItems || []).filter(si => !si.hideForRoles || !si.hideForRoles.includes(user?.role));

  // Grup -> öğeleri
  const groupItems = (groupKey) => navigation.filter(it => it.group === groupKey && (it.module !== 'dashboard' || it.href === '/kule'));
  // 2026-07-31 FIX: grup rozeti AYNI modülü iki kez saymasın. Örn. Sevkiyat grubunda
  // /shipping ve /shipping/handover ikisi de module='shipping' → to_ship (10046) iki kez
  // toplanıp 20092 gösteriyordu. Artık modül bazında dedup → gerçek tekil sayı.
  const groupBadge = (groupKey) => {
    const seen = new Set();
    return groupItems(groupKey).reduce((sum, it) => {
      if (it.module && seen.has(it.module)) return sum;
      if (it.module) seen.add(it.module);
      return sum + itemBadge(it);
    }, 0);
  };
  const groupHasContent = (groupKey) => groupItems(groupKey).length > 0;

  // Aktif grup tespiti (mevcut rota hangi gruba ait)
  const pathInItem = (item) => {
    if (item.subItems) return visibleSubs(item).some(si => location.pathname === si.href || location.pathname.startsWith(si.href + '/'));
    return location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
  };
  const activeGroupKey = location.pathname === '/' ? 'genel' : (navigation.find(pathInItem)?.group || null);

  // Global arama indeksi (yaprak sayfalar)
  const searchIndex = [];
  navigation.forEach(it => {
    if (it.subItems) visibleSubs(it).forEach(si => searchIndex.push({ name: `${it.name} · ${si.name}`, href: si.href }));
    else if (it.href) searchIndex.push({ name: it.name, href: it.href });
  });
  const norm = (s) => (s || '').toLocaleLowerCase('tr-TR');
  const searchResults = searchQuery.trim()
    ? searchIndex.filter(r => norm(r.name).includes(norm(searchQuery))).slice(0, 8)
    : [];
  const goSearch = (href) => { setSearchQuery(''); setSearchOpen(false); navigate(href); };

  // Alt-menü açma (mobil ve mega-menü için)
  useEffect(() => {
    const menusToExpand = {};
    allNavigation.forEach(item => {
      if (item.subItems && item.subItems.some(si => location.pathname.startsWith(si.href))) menusToExpand[item.name] = true;
    });
    if (Object.keys(menusToExpand).length > 0) setExpandedMenus(prev => ({ ...prev, ...menusToExpand }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const roleLabel = user?.display_role || t(`roles.${user?.role}`, user?.role);

  // Mega-menü içeriği (bir grubun öğeleri, alt-menüler bölüm olarak)
  const renderMegaMenu = (groupKey) => {
    const items = groupItems(groupKey);
    if (items.length === 0) return null;
    const path = location.pathname;
    return (
      <div className="columns-1 sm:columns-2 gap-x-6 [column-fill:_balance]">
        {items.map((item) => {
          const Icon = item.icon;
          const badge = itemBadge(item);
          if (item.subItems) {
            const subs = visibleSubs(item);
            return (
              <div key={item.name} className="mb-4 break-inside-avoid">
                <div className="flex items-center gap-2 px-2 mb-1.5">
                  <Icon className="w-4 h-4 text-brand-600" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{item.name}</span>
                  {badge > 0 && <span className="ml-auto text-[10px] font-bold text-white bg-danger rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge}</span>}
                </div>
                <div className="space-y-0.5">
                  {subs.map(si => {
                    const active = path === si.href || path.startsWith(si.href + '/');
                    return (
                      <Link key={si.href} to={si.href} onClick={closeAllMenus}
                        className={`block pl-8 pr-3 py-1.5 rounded-md text-[13px] transition-colors ${active ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                        {si.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          }
          const active = path === item.href || (item.href !== '/' && path.startsWith(item.href + '/'));
          return (
            <Link key={item.href} to={item.href} onClick={closeAllMenus}
              className={`flex items-center gap-2.5 px-2 py-2 mb-1 rounded-md break-inside-avoid transition-colors ${active ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-slate-700 hover:bg-slate-100'}`}>
              <Icon className={`w-4 h-4 ${active ? 'text-brand-600' : 'text-slate-400'}`} />
              <span className="text-[13px] flex-1 truncate">{item.name}</span>
              {badge > 0 && <span className="text-[10px] font-bold text-white bg-danger rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge}</span>}
            </Link>
          );
        })}
      </div>
    );
  };

  const iconBtn = 'p-2 rounded-lg text-shell-fg/80 hover:text-white hover:bg-shell-hover transition-colors';

  return (
    <div className="min-h-screen bg-canvas">
      {/* ══ SHELL BAR ══ */}
      <header className="sticky top-0 z-40 bg-shell text-shell-fg">
        <div className="h-12 px-3 sm:px-4 flex items-center gap-2">
          {/* Mobil menü */}
          <button onClick={() => setMobileNavOpen(true)} className={`${iconBtn} lg:hidden`} aria-label="Menü">
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); try { window.location.reload(); } catch (_) { window.location.href = '/'; } }}
            className="flex items-center shrink-0 mr-1" title="Ana sayfa">
            {/* İt-fix (2026-07-14): dar/mobil ekranda header taşmasını önle — mobilde sadece marka ikonu, sm+ tam logo */}
            <DiyalogLogo size="sm" variant="dark" markOnly className="sm:hidden" />
            <DiyalogLogo size="sm" variant="dark" className="hidden sm:inline-flex" />
          </a>

          {/* Global arama (Fiori shell ortası) */}
          <div className="flex-1 min-w-0 flex justify-center px-2" ref={searchRef}>
            <div className="relative w-full max-w-[460px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-shell-muted pointer-events-none" />
              <input
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Enter' && searchResults[0]) goSearch(searchResults[0].href); if (e.key === 'Escape') setSearchOpen(false); }}
                placeholder="Sayfa ara: sipariş, toplama, sevkiyat, stok…"
                className="w-full h-8 pl-9 pr-3 rounded-md bg-white/10 text-sm text-white placeholder:text-shell-muted border border-transparent focus:bg-white focus:text-ink focus:placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/40 transition"
              />
              {searchOpen && searchQuery.trim() && (
                <div className="absolute left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-hairline overflow-hidden z-50">
                  {searchResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-slate-400">Sonuç yok</div>
                  ) : searchResults.map((r, i) => (
                    <button key={r.href + i} onMouseDown={(e) => { e.preventDefault(); goSearch(r.href); }}
                      className="flex items-center w-full gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-brand-50 hover:text-brand-700">
                      <Search className="w-3.5 h-3.5 text-slate-300" />
                      <span className="flex-1 truncate">{r.name}</span>
                      {i === 0 && <CornerDownLeft className="w-3.5 h-3.5 text-slate-300" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sağ aksiyonlar */}
          <div className="flex items-center gap-0.5 shrink-0">
            <DevModeBadge />
            <button onClick={() => window.open('/leaderboard/picking', '_blank')} className={`${iconBtn} hidden md:inline-flex`} title={t('leaderboard.pickingTitle')}>
              <ShoppingCart className="w-5 h-5" />
            </button>
            <button onClick={() => window.open('/leaderboard/packing', '_blank')} className={`${iconBtn} hidden md:inline-flex`} title={t('leaderboard.packingTitle')}>
              <Package className="w-5 h-5" />
            </button>

            {/* Bildirim */}
            <div className="relative" ref={notificationRef}>
              <button onClick={() => { setNotificationOpen(!notificationOpen); setProfileOpen(false); setLangOpen(false); setOpenGroup(null); }} className={`${iconBtn} relative`}>
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-danger rounded-full ring-2 ring-shell">{unreadCount}</span>
                )}
              </button>
              {notificationOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-hairline z-50 overflow-hidden text-ink">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">{t('common.notifications')}</h3>
                    {unreadCount > 0 && <span className="text-xs text-brand-600 font-medium">{unreadCount} {t('common.unread')}</span>}
                  </div>
                  <div className="max-h-96 overflow-y-auto custom-scrollbar">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">{t('common.noNotifications')}</div>
                    ) : notifications.map((n) => (
                      <div key={n.id} onClick={() => { markNotificationRead(n); if (n.url) { setNotificationOpen(false); navigate(n.url); } }}
                        className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${n.is_read ? 'opacity-55' : 'bg-brand-50/50'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`shrink-0 w-2 h-2 mt-1.5 rounded-full ${getNotificationColor(n.type)}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                            <p className="text-[11px] text-slate-400 mt-1">{getRelativeTime(n.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Dil */}
            <div className="relative" ref={langRef}>
              <button onClick={() => { setLangOpen(!langOpen); setNotificationOpen(false); setProfileOpen(false); setOpenGroup(null); }} className={`${iconBtn} flex items-center gap-1`} title={t('profile.language')}>
                <Globe className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase hidden sm:inline">{i18n.language}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-hairline z-50 py-1 text-ink">
                  {[{ code: 'tr', label: 'Türkçe' }, { code: 'en', label: 'English' }, { code: 'ar', label: 'العربية' }].map((lang) => (
                    <button key={lang.code} onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                      className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${i18n.language === lang.code ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>
                      <span className="uppercase text-xs font-bold w-6 mr-2 text-slate-400">{lang.code}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profil */}
            <div className="relative" ref={profileRef}>
              <button onClick={() => { setProfileOpen(!profileOpen); setNotificationOpen(false); setLangOpen(false); setOpenGroup(null); }}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-shell-hover transition-colors">
                <div className="flex items-center justify-center w-8 h-8 bg-brand-500 text-white rounded-full font-semibold text-sm ring-2 ring-white/10 shrink-0">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="hidden lg:block text-left leading-tight">
                  <p className="text-[13px] font-semibold text-white">{user?.name}</p>
                  <p className="text-[11px] text-shell-muted">{roleLabel}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-shell-muted hidden lg:block" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-hairline z-50 overflow-hidden text-ink">
                  <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-700 text-white rounded-xl font-bold text-lg shrink-0">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                      <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                      <p className="text-xs text-brand-600 font-medium mt-0.5">{roleLabel}</p>
                    </div>
                  </div>
                  <div className="py-1.5">
                    <button onClick={() => { setProfileOpen(false); navigate('/profile'); }} className="flex items-center w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                      <User className="w-4 h-4 mr-3 text-slate-400" />{t('navigation.profile')}
                    </button>
                  </div>
                  <div className="border-t border-slate-100 py-1.5">
                    <button onClick={() => { setProfileOpen(false); handleLogout(); }} className="flex items-center w-full px-4 py-2.5 text-sm text-danger hover:bg-danger/5">
                      <LogOut className="w-4 h-4 mr-3" />{t('auth.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ LAUNCHPAD GRUP ŞERİDİ (yatay) ══ */}
        <div className="hidden lg:block border-t border-shell-border" ref={groupNavRef}>
          <nav className="h-11 px-2 flex items-center gap-0.5">
            {NAV_GROUPS.map((g) => {
              const Icon = g.icon;
              if (g.home) {
                const active = activeGroupKey === g.key;
                return (
                  <Link key={g.key} to={g.home} onClick={closeAllMenus}
                    className={`relative flex items-center gap-2 h-11 px-3 text-[13px] font-medium transition-colors ${active ? 'text-white' : 'text-shell-fg/75 hover:text-white'}`}>
                    <Icon className="w-4 h-4" />{g.label}
                    {active && <span className="absolute left-2 right-2 bottom-0 h-[3px] rounded-t bg-accentblue" />}
                  </Link>
                );
              }
              if (!groupHasContent(g.key)) return null;
              const active = activeGroupKey === g.key;
              const open = openGroup === g.key;
              const badge = groupBadge(g.key);
              return (
                <div key={g.key} className="relative">
                  <button onClick={() => setOpenGroup(open ? null : g.key)}
                    className={`relative flex items-center gap-2 h-11 px-3 text-[13px] font-medium transition-colors ${active || open ? 'text-white' : 'text-shell-fg/75 hover:text-white'}`}>
                    <Icon className="w-4 h-4" />{g.label}
                    {badge > 0 && <span className="text-[10px] font-bold text-white bg-danger rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge}</span>}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                    {active && <span className="absolute left-2 right-2 bottom-0 h-[3px] rounded-t bg-accentblue" />}
                  </button>
                  {open && (
                    <div className="absolute left-0 top-full mt-0 w-[min(620px,92vw)] bg-white rounded-b-xl shadow-2xl border border-hairline border-t-0 z-50 p-4 text-ink">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                        <Icon className="w-4 h-4 text-brand-600" />
                        <span className="text-sm font-semibold text-ink">{g.label}</span>
                      </div>
                      {renderMegaMenu(g.key)}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </header>

      {/* ══ MOBİL SLIDE-OVER NAV ══ */}
      {mobileNavOpen && (
        <>
          <div className="fixed inset-0 bg-ink/50 z-50 lg:hidden" onClick={() => setMobileNavOpen(false)} />
          <aside className={`fixed top-0 ${isRtl ? 'right-0' : 'left-0'} h-full w-72 bg-white z-50 lg:hidden flex flex-col shadow-2xl`}>
            <div className="h-12 px-4 bg-shell flex items-center justify-between shrink-0">
              <DiyalogLogo size="sm" variant="dark" />
              <button onClick={() => setMobileNavOpen(false)} className="p-2 text-shell-fg hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto custom-scrollbar p-3">
              <Link to="/" onClick={() => setMobileNavOpen(false)}
                className={`flex items-center gap-3 h-10 px-3 rounded-lg mb-2 ${location.pathname === '/' ? 'bg-brand-600 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'}`}>
                <LayoutDashboard className="w-[18px] h-[18px]" />Genel Bakış
              </Link>
              {NAV_GROUPS.filter(g => !g.home && groupHasContent(g.key)).map(g => (
                <div key={g.key} className="mb-1">
                  <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <g.icon className="w-3.5 h-3.5" />{g.label}
                  </div>
                  {groupItems(g.key).map(item => {
                    const Icon = item.icon;
                    const badge = itemBadge(item);
                    if (item.subItems) {
                      const subs = visibleSubs(item);
                      const exp = expandedMenus[item.name];
                      const subActive = subs.some(si => location.pathname.startsWith(si.href));
                      return (
                        <div key={item.name}>
                          <button onClick={() => toggleMenu(item.name)}
                            className={`flex items-center w-full h-10 px-3 rounded-lg ${subActive ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}>
                            <Icon className="w-[18px] h-[18px] mr-3 text-slate-400" />
                            <span className="flex-1 text-left text-[14px]">{item.name}</span>
                            {badge > 0 && <span className="bg-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full mr-1.5">{badge}</span>}
                            {exp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          {exp && (
                            <div className="ml-[26px] pl-3 border-l border-slate-200 my-0.5 space-y-0.5">
                              {subs.map(si => {
                                const a = location.pathname === si.href || location.pathname.startsWith(si.href + '/');
                                return (
                                  <Link key={si.href} to={si.href} onClick={() => setMobileNavOpen(false)}
                                    className={`block px-3 h-9 leading-9 rounded-lg text-[13px] ${a ? 'bg-brand-600 text-white font-semibold' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    {si.name}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    const a = location.pathname === item.href;
                    return (
                      <Link key={item.href} to={item.href} onClick={() => setMobileNavOpen(false)}
                        className={`flex items-center h-10 px-3 rounded-lg ${a ? 'bg-brand-600 text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}>
                        <Icon className="w-[18px] h-[18px] mr-3 text-slate-400" />
                        <span className="text-[14px] flex-1">{item.name}</span>
                        {badge > 0 && <span className="bg-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </nav>
          </aside>
        </>
      )}

      {/* ══ İÇERİK ══ */}
      <div>
        {user && user.role === 'Developer' && (
          <Suspense fallback={null}><DeveloperDocEditorLazy /></Suspense>
        )}
        <main className="px-3 sm:px-6 lg:px-8 py-5 sm:py-6 max-w-[1480px] mx-auto">
          {children}
        </main>
      </div>

      {/* Kiosk Unlock Modal */}
      {kioskUnlocked && (
        <div className="fixed inset-0 bg-ink/70 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Kiosk Kilidi Açıldı</h2>
            <p className="text-sm text-slate-500 mb-6">{kioskUnlockName}</p>
            <div className="space-y-2">
              <button onClick={() => { fetch('http://localhost:9999/kill-chrome', { mode: 'no-cors' }).catch(() => {}); setTimeout(() => { try { window.close(); } catch (e) {} }, 500); }}
                className="w-full h-12 bg-danger hover:bg-danger/90 text-white rounded-xl font-semibold text-sm transition-colors">
                Tarayıcıyı Kapat
              </button>
              <button onClick={() => setKioskUnlocked(false)}
                className="w-full h-10 border border-slate-300 text-slate-600 rounded-xl font-medium text-sm hover:bg-slate-50 transition-colors">
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}
      <ScrollToTop />
    </div>
  );
}
